import CopyIcon from "@mui/icons-material/FileCopyOutlined";
import { IconButton } from "@mui/material";
import React from "react";

import DescriptiveTooltip from "../../components/DescriptiveTooltip";
import { useAppDispatch } from "../../store/hooks";
import { addSnackbarQueueItem } from "../../store/slices/snackbarSlice";

type Props = {
  handleCopyEvent?: boolean;
  text: string;
};

const clipboardAvailable = navigator.clipboard !== undefined;

function CopyButton({ handleCopyEvent, text }: Props) {
  const dispatch = useAppDispatch();

  const copyToClipboard = React.useCallback(
    (e: ClipboardEvent | React.MouseEvent) => {
      if (!clipboardAvailable) {
        dispatch(
          addSnackbarQueueItem({
            message: `JavaScript can't access the clipboard at ${window.location.origin}. Try copying manually.`,
            variant: "error",
          }),
        );
        return;
      }
      e.preventDefault();
      navigator.clipboard
        .writeText(text)
        .then(() => {
          dispatch(
            addSnackbarQueueItem({ message: "Copied!", variant: "primary" }),
          );
        })
        .catch(() => {
          dispatch(
            addSnackbarQueueItem({ message: "Copy error", variant: "error" }),
          );
        });
    },
    [text, dispatch],
  );

  React.useEffect(() => {
    if (handleCopyEvent && clipboardAvailable) {
      document.addEventListener("copy", copyToClipboard);
      return () => document.removeEventListener("copy", copyToClipboard);
    }
  }, [handleCopyEvent, copyToClipboard]);

  return (
    <DescriptiveTooltip title="Copy to clipboard">
      <IconButton onClick={copyToClipboard} size="small">
        <CopyIcon fontSize="inherit" />
      </IconButton>
    </DescriptiveTooltip>
  );
}

export default CopyButton;
